import { BlankSlate } from "@msuite/ui/blank-slate";
import { ContentCard } from "@msuite/ui/content-card";
import { Divider } from "@msuite/ui/divider";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { responseHandler } from "@msuite/ui/utils";
import type { PostNoteForm } from "@msuite/utils/schemas";
import type { NotesTable, UploadResult } from "@msuite/utils/types";
import { useLayoutEffect, useRef, useState } from "react";
import { NoteForm } from "@/forms/note-form";
import { client } from "@/hono";
import { useNoteCapabilities } from "@/hooks/use-note-capabilities";
import { useNotes } from "@/hooks/use-notes";
import { Note } from "./note";
import type { ClassNameProps } from "@msuite/ui/types";

/** Props Interface */
interface NotesProps extends Partial<ClassNameProps> {
	notesTable: NotesTable;
	/**
	 * This should be the identifier the given notes table is using -> projectId, contactId, etc.
	 */
	identifier: string;
	/**
	 * The parent row itself, where the page has it. Reading and writing a note follow the parent's
	 * own read right, and a rule narrower than the organisation — a calculation of one's own, a
	 * project one is on — can only be answered against the row. Left out, the check is the category
	 * one and the server still decides.
	 */
	parent?: Record<string, unknown> | null;
	withIsImportant?: boolean;
	isImportantLabel?: string;
	withIsImportantIsDisabled?: boolean;
	draggable?: boolean;
	contentClassName?: ClassNameProps["className"];
}

export function Notes({
	notesTable,
	identifier,
	parent,
	withIsImportant,
	isImportantLabel,
	withIsImportantIsDisabled,
	draggable = false,
	className,
	contentClassName,
}: NotesProps) {
	/** Hooks */
	const { t } = useTranslation();
	const scrollRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const isAutoScrollEnabled = useRef(true);
	const noteCapabilities = useNoteCapabilities();
	const canReadParent = noteCapabilities.canReadParent(notesTable, parent);
	const rulesAreLoading = noteCapabilities.rulesAreLoading;

	/** State */
	const [markedAsReply, setMarkedAsReply] = useState<string | null>(null);

	/** Query */
	const { notes, refetch, isError } = useNotes(notesTable, identifier, { enabled: canReadParent });

	/** Effects */
	useLayoutEffect(() => {
		const scrollNode = scrollRef.current;
		const contentNode = contentRef.current;
		if (!scrollNode || !contentNode) return;
		function scrollToBottomIfEnabled() {
			if (!scrollNode || !isAutoScrollEnabled.current) return;
			scrollNode.scrollTop = scrollNode.scrollHeight;
		}
		scrollToBottomIfEnabled();
		const observer = new ResizeObserver(scrollToBottomIfEnabled);
		observer.observe(contentNode);
		return () => observer.disconnect();
	}, [notes]);

	/** Functions */
	function handleOnScroll() {
		const node = scrollRef.current;
		if (!node) return;
		isAutoScrollEnabled.current = node.scrollHeight - node.scrollTop - node.clientHeight < 8;
	}

	async function handleOnPostNote(json: PostNoteForm, uploadResults: UploadResult[] | null) {
		const response = await client.notes[":notesTable"][":identifier"].$post({
			json: {
				...json,
				uploadResults,
			},
			param: {
				notesTable,
				identifier,
			},
		});
		const data = await responseHandler(response);
		return data.note;
	}

	/** Computed */
	// One writes a note on anything one can see, so writing asks two things: the caller's own
	// `create Note` rule, and the parent's read right — the same pair the route checks, in the same
	// order. Being on the page is not the answer to the second one: a surface can sit on a page
	// whose parent this caller may not read.
	const hasAccess = canReadParent && !isError;
	const canWriteNotes = hasAccess && noteCapabilities.canCreate;
	const hasNotes = notes && notes.length > 0;
	const parentNotes = notes?.filter((note) => !note.parentNoteId) ?? [];
	const childNotes = notes?.filter((note) => !!note.parentNoteId) ?? [];

	/** Render */
	return (
		<ContentCard
			title={`${t("general.notes")} (${hasNotes ? notes.length : 0})`}
			icon={icon("MessageSquareText")}
			layout={false}
			draggable={draggable}
			className={className}
			contentClassName={contentClassName}
		>
			{rulesAreLoading ? (
				// A rule list in flight is not a refusal. The lock below says "you may not see
				// this", which is a claim the page cannot make until the rules have answered.
				<BlankSlate
					withBorder={false}
					withAccessory={false}
					isLoading
					label={t("general.loading")}
				/>
			) : !hasAccess ? (
				<BlankSlate
					withBorder={false}
					withAccessory={false}
					Icon={icon("Lock")}
					label={t("notes.no_access")}
				/>
			) : hasNotes ? (
				<div
					ref={scrollRef}
					onScroll={handleOnScroll}
					className="flex flex-col -m-4 -mb-2 max-h-[400px] overflow-y-auto"
				>
					<div
						ref={contentRef}
						className="flex flex-col"
					>
						{parentNotes.map((note) => (
							<Note
								key={note.id}
								note={note}
								onMarkAsReply={setMarkedAsReply}
								childNotes={childNotes}
								onCompleteEdit={refetch}
								canReply={canWriteNotes}
								withIsImportant={withIsImportant}
								isImportantLabel={isImportantLabel}
								withIsImportantIsDisabled={withIsImportantIsDisabled}
							/>
						))}
					</div>
				</div>
			) : (
				<BlankSlate
					withBorder={false}
					withAccessory={false}
				/>
			)}
			{canWriteNotes && (
				<div className="-mx-4">
					<Divider />
				</div>
			)}
			{canWriteNotes && (
				<NoteForm
					postFn={handleOnPostNote}
					refetch={refetch}
					markedAsReply={markedAsReply}
					resetMarkedAsReply={setMarkedAsReply}
					notesTable={notesTable}
					identifier={identifier}
					withIsImportant={withIsImportant}
					isImportantLabel={isImportantLabel}
					withIsImportantIsDisabled={withIsImportantIsDisabled}
				/>
			)}
		</ContentCard>
	);
}
